import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type { IndexOrigin } from './index-origin';
import type { RoomKind } from './room';

/**
 * O fator próprio de uma peça desenhada ou de um cômodo (Fase 39, etapa 3 — D14, RN-511 a RN-515).
 *
 * A medida do serviço é sempre a da planta e não muda (D13). O que muda, por peça ou por cômodo, é
 * o fator que transforma essa medida em material, insumo ou mão de obra: a estaca E12, que pegou
 * água, leva 1,10 de concreto por m³ no lugar do 1,05 do tipo; o banheiro leva outro fator de
 * argamassa no piso que a sala. O fator próprio fica **por cima** do fator do tipo (na estaca) ou
 * do índice do item na obra (no cômodo), e mudar o número não pede motivo (D9, RN-513).
 *
 * Mora numa tabela de exceção, só com o que foi decidido (ADR-181): a peça e o cômodo sem fator
 * próprio seguem o do tipo ou o da obra, e é a ausência que diz isso.
 */

/** O fator de um item, pela chave do serviço e a do item — as mesmas do catálogo da obra. */
export interface ItemFactor {
  readonly serviceKey: string;
  readonly itemKey: string;
  /** Quanto do item por unidade da medida do serviço na peça ou no cômodo. Maior que zero. */
  readonly factor: number;
  /**
   * De onde veio o fator do cômodo (Fase 40, RN-527): `MEDIA` é a média do tipo de cômodo, que a
   * tela grava e o "Atualizar" troca; `DIGITADO` é de alguém. Ausente é o de antes: digitado. A
   * estaca não usa.
   */
  readonly origin?: IndexOrigin;
  /** A foto da média do tipo de cômodo quando o fator foi gravado (D8). */
  readonly average?: RoomKindAverage;
  /**
   * A foto da média na linha de um tipo de bloco, de pilar ou de viga (Fase 42, etapa 4 — G10,
   * G11; ADR-191): o fator médio do dia em que a linha nasceu ou foi atualizada. Com `origin`
   * `MEDIA`, a linha segue a média — não entra na média das outras obras (D5) e recebe "A média
   * mudou"; `DIGITADO` é o número de alguém, com a média ao lado. O cômodo usa `average`.
   */
  readonly typeAverage?: TypeLineAverage;
}

/** A foto da média na linha de um tipo (Fase 42, ADR-191, `0099`): de quantas obras e peças. */
export interface TypeLineAverage {
  readonly rate: number;
  /** `aaaa-mm-dd`. */
  readonly at: string;
  readonly projects: number;
  readonly pieces: number;
}

/**
 * A foto da média de um tipo de cômodo (Fase 40, RN-527): o valor, o dia, de onde veio e de que
 * tipo. O tipo é o que deixa a tela saber que o cômodo mudou de tipo e trocar o fator (RN-528).
 */
export interface RoomKindAverage {
  readonly rate: number;
  /** `aaaa-mm-dd`. */
  readonly at: string;
  readonly projects: number;
  readonly rooms: number;
  readonly roomKind: RoomKind;
}

function mesmaFoto(a?: RoomKindAverage, b?: RoomKindAverage): boolean {
  if (a === undefined || b === undefined) return a === b;
  return (
    a.rate === b.rate &&
    a.at === b.at &&
    a.projects === b.projects &&
    a.rooms === b.rooms &&
    a.roomKind === b.roomKind
  );
}

export const OWN_FACTOR_ERROR = {
  INVALID_FACTOR: 'TAKEOFF.RATE_AVERAGE_INVALID_FACTOR',
} as const;

/**
 * A chave de um item no fator próprio: a mesma do lançamento (`itemQuantityKey`) e da composição
 * do tipo (`pileRateKey`), para a conta achar os três pelo mesmo nome.
 */
export function ownFactorKey(serviceKey: string, itemKey: string): string {
  return `${serviceKey}»${itemKey}`;
}

/** Os fatores pela chave do item — o que a conta lê. Vazio quando não há nenhum. */
export function ownFactorsByKey(
  factors: readonly ItemFactor[] | undefined,
): Readonly<Record<string, number>> {
  const porChave: Record<string, number> = {};
  for (const linha of factors ?? []) {
    porChave[ownFactorKey(linha.serviceKey, linha.itemKey)] = linha.factor;
  }
  return porChave;
}

/**
 * As duas listas dizem o mesmo — a mesma chave com o mesmo fator, em qualquer ordem? Ausente e
 * vazia são a mesma coisa: nenhum fator próprio.
 */
export function sameItemFactors(
  a: readonly ItemFactor[] | undefined,
  b: readonly ItemFactor[] | undefined,
): boolean {
  const de = ownFactorsByKey(a);
  const para = ownFactorsByKey(b);
  const chaves = Object.keys(de);
  if (
    chaves.length !== Object.keys(para).length ||
    !chaves.every((chave) => para[chave] === de[chave])
  ) {
    return false;
  }
  // A origem e a foto também (Fase 40): o "Usar a média" muda a origem sem mudar o número.
  const linhasDe = new Map((a ?? []).map((l) => [ownFactorKey(l.serviceKey, l.itemKey), l]));
  return (b ?? []).every((l) => {
    const outra = linhasDe.get(ownFactorKey(l.serviceKey, l.itemKey));
    return outra?.origin === l.origin && mesmaFoto(outra?.average, l.average);
  });
}

/** O fator próprio de um item, quando há. */
export function ownFactorOf(
  factors: readonly ItemFactor[] | undefined,
  serviceKey: string,
  itemKey: string,
): number | undefined {
  return factors?.find((linha) => linha.serviceKey === serviceKey && linha.itemKey === itemKey)
    ?.factor;
}

/**
 * Os fatores próprios conferidos: maior que zero, finito e um por item (RN-511, RN-512).
 *
 * Zero não é "consome nada": é o "não preenchido" do índice (RN-281), e um zero aqui apagaria o
 * item da peça sem ninguém ver. Quem quer voltar ao do tipo tira o fator — não zera.
 */
export function checkItemFactors(
  factors: readonly ItemFactor[],
): Result<readonly ItemFactor[], DomainError> {
  const vistos = new Set<string>();
  for (const linha of factors) {
    const chave = ownFactorKey(linha.serviceKey, linha.itemKey);
    if (
      linha.serviceKey.trim() === '' ||
      linha.itemKey.trim() === '' ||
      !Number.isFinite(linha.factor) ||
      linha.factor <= 0 ||
      vistos.has(chave)
    ) {
      return err(
        domainError(
          OWN_FACTOR_ERROR.INVALID_FACTOR,
          'O fator próprio de cada item deve ser maior que zero e aparecer uma vez só.',
          { serviceKey: linha.serviceKey, itemKey: linha.itemKey, factor: linha.factor },
        ),
      );
    }
    vistos.add(chave);
  }
  return ok(factors);
}

const DIA = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A origem e a foto que a linha de um tipo de bloco, de pilar ou de viga pode guardar, já
 * conferidas (Fase 42, etapa 4 — ADR-191, RN-583, `0099`). É a regra do índice do item
 * (`indexProvenance`) e da taxa da disciplina (`steelRateProvenance`):
 *
 * - a foto fora de forma (valor não positivo, dia que não é dia, contagem que não é inteira) é
 *   descartada, e o fator fica — seis casas no valor, como a coluna;
 * - a origem `MEDIA` sem a foto vira `DIGITADO`: é o número de alguém, e a média dele não se sabe
 *   (a `0099` recusa a `MEDIA` sem foto).
 */
export function typeLineProvenance(
  origin: IndexOrigin | undefined,
  average: TypeLineAverage | undefined,
): { readonly origin?: IndexOrigin; readonly typeAverage?: TypeLineAverage } {
  const foto =
    average !== undefined &&
    Number.isFinite(average.rate) &&
    average.rate > 0 &&
    DIA.test(average.at) &&
    Number.isInteger(average.projects) &&
    average.projects >= 0 &&
    Number.isInteger(average.pieces) &&
    average.pieces >= 0
      ? {
          rate: Math.round(average.rate * 1e6) / 1e6,
          at: average.at,
          projects: average.projects,
          pieces: average.pieces,
        }
      : undefined;
  const origem: IndexOrigin | undefined =
    origin === 'MEDIA' && foto === undefined ? 'DIGITADO' : origin;
  return {
    ...(origem === undefined ? {} : { origin: origem }),
    ...(foto === undefined ? {} : { typeAverage: foto }),
  };
}

/**
 * A composição de um tipo de bloco, de pilar ou de viga, conferida (Fase 42, etapa 4 — RN-582,
 * RN-583): o fator como o próprio da peça (`checkItemFactors`: positivo, um por item), e a origem e
 * a foto de cada linha pela regra de `typeLineProvenance`. A foto do cômodo (`average`) não é de
 * tipo e não passa.
 */
export function checkTypeLines(
  rates: readonly ItemFactor[],
): Result<readonly ItemFactor[], DomainError> {
  const conferidos = checkItemFactors(rates);
  if (!conferidos.ok) return conferidos;
  return ok(
    conferidos.value.map((linha) => ({
      serviceKey: linha.serviceKey,
      itemKey: linha.itemKey,
      factor: linha.factor,
      ...typeLineProvenance(linha.origin, linha.typeAverage),
    })),
  );
}

/**
 * A lista com o fator de um item trocado — ou tirado, quando `factor` é `undefined`: a volta ao
 * do tipo, ou ao índice da obra (RN-513). O fator do mesmo item que já estava sai antes de o novo
 * entrar: um por item.
 */
export function withOwnFactor(
  factors: readonly ItemFactor[] | undefined,
  serviceKey: string,
  itemKey: string,
  factor: number | undefined,
  origem: Pick<ItemFactor, 'origin' | 'average'> = {},
): readonly ItemFactor[] {
  const outros = (factors ?? []).filter(
    (linha) => !(linha.serviceKey === serviceKey && linha.itemKey === itemKey),
  );
  if (factor === undefined) return outros;
  return [
    ...outros,
    {
      serviceKey,
      itemKey,
      factor,
      ...(origem.origin === undefined ? {} : { origin: origem.origin }),
      ...(origem.average === undefined ? {} : { average: origem.average }),
    },
  ];
}
